import React, { useRef, useState, useEffect } from "react";
import { Button, Spinner, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import Webcam from "react-webcam";
import jsQR from "jsqr";

export default function ScannerCamera({ onScan, loading, mode }) {
    const webcamRef = useRef(null);
    const [cameras, setCameras] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState(null);
    const [scanning, setScanning] = useState(true);
    const [error, setError] = useState(null);
    const [torchOn, setTorchOn] = useState(false);
    const [hasTorch, setHasTorch] = useState(false);
    const [lastScan, setLastScan] = useState(null);
    const scanInterval = useRef(null);

    useEffect(() => {
        const getCameras = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter(device => device.kind === 'videoinput');
                setCameras(videoDevices);
                if (videoDevices.length > 0) {
                    setSelectedCamera(videoDevices[0].deviceId);
                }
            } catch (err) {
                setError("Impossible d'accéder aux caméras");
            }
        };
        
        getCameras();
        
        return () => {
            if (scanInterval.current) {
                clearInterval(scanInterval.current);
            }
        };
    }, []);

    useEffect(() => {
        if (scanning && webcamRef.current && webcamRef.current.video) {
            scanInterval.current = setInterval(() => {
                const video = webcamRef.current.video;
                if (video && video.readyState === video.HAVE_ENOUGH_DATA) {
                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const code = jsQR(imageData.data, canvas.width, canvas.height);
                    
                    if (code && code.data !== lastScan) {
                        setLastScan(code.data);
                        onScan(code.data);
                    }
                }
            }, 300);
        }
        
        return () => {
            if (scanInterval.current) {
                clearInterval(scanInterval.current);
            }
        };
    }, [scanning, lastScan, onScan]);

    const videoConstraints = {
        deviceId: selectedCamera,
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: "environment"
    };

    const toggleTorch = async () => {
        if (webcamRef.current && webcamRef.current.video) {
            try {
                const stream = webcamRef.current.video.srcObject;
                const track = stream.getVideoTracks()[0];
                
                // Vérifier si le mode torche est supporté
                const capabilities = track.getCapabilities?.();
                if (capabilities && capabilities.torch) {
                    await track.applyConstraints({
                        advanced: [{ torch: !torchOn }]
                    });
                    setTorchOn(!torchOn);
                    setHasTorch(true);
                } else {
                    setHasTorch(false);
                }
            } catch (err) {
                console.error("Erreur torche:", err);
                setHasTorch(false);
            }
        }
    };

    const getModeText = () => {
        switch(mode) {
            case 'entree': return 'Scannez pour ENTRÉE de stock';
            case 'sortie': return 'Scannez pour SORTIE de stock';
            default: return 'Scannez pour RECHERCHER un produit';
        }
    };

    return (
        <div className="p-3">
            {/* Message selon le mode */}
            <div className={`alert alert-${mode === 'entree' ? 'success' : mode === 'sortie' ? 'danger' : 'info'} mb-3`}>
                <i className={`ri-${mode === 'entree' ? 'add-circle' : mode === 'sortie' ? 'subtract' : 'search'}-line me-2`}></i>
                {getModeText()}
            </div>

            {/* Sélecteur de caméra */}
            {cameras.length > 1 && (
                <div className="mb-3">
                    <select 
                        className="form-select"
                        value={selectedCamera || ''}
                        onChange={(e) => setSelectedCamera(e.target.value)}
                    >
                        {cameras.map((camera, index) => (
                            <option key={camera.deviceId} value={camera.deviceId}>
                                Caméra {index + 1}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Zone de scan */}
            <div className="scanner-container position-relative mb-3" style={{ minHeight: '300px' }}>
                {selectedCamera && (
                    <>
                        <Webcam
                            ref={webcamRef}
                            audio={false}
                            screenshotFormat="image/jpeg"
                            videoConstraints={videoConstraints}
                            style={{
                                width: '100%',
                                height: 'auto',
                                borderRadius: '8px'
                            }}
                        />
                        
                        {/* Overlay de scan */}
                        <div className="scan-overlay position-absolute top-0 start-0 w-100 h-100">
                            <div className="scan-region position-absolute top-50 start-50 translate-middle">
                                <div className="scan-corner scan-corner-tl"></div>
                                <div className="scan-corner scan-corner-tr"></div>
                                <div className="scan-corner scan-corner-bl"></div>
                                <div className="scan-corner scan-corner-br"></div>
                                <div className="scan-line"></div>
                            </div>
                        </div>
                    </>
                )}

                {loading && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50">
                        <Spinner animation="border" variant="light" />
                    </div>
                )}
            </div>

            {/* Contrôles */}
            <div className="d-flex justify-content-between align-items-center">
                <div>
                    <OverlayTrigger 
                        placement="top" 
                        overlay={<Tooltip>Activer/Désactiver le scan</Tooltip>}
                    >
                        <Button 
                            variant={scanning ? "success" : "secondary"}
                            onClick={() => setScanning(!scanning)}
                            className="me-2"
                        >
                            <i className={`ri-${scanning ? 'pause' : 'play'}-circle-line me-2`}></i>
                            {scanning ? 'Pause' : 'Reprendre'}
                        </Button>
                    </OverlayTrigger>

                    {hasTorch && (
                        <OverlayTrigger 
                            placement="top" 
                            overlay={<Tooltip>Lampe torche</Tooltip>}
                        >
                            <Button 
                                variant={torchOn ? "warning" : "outline-secondary"}
                                onClick={toggleTorch}
                            >
                                <i className="ri-flashlight-line"></i>
                            </Button>
                        </OverlayTrigger>
                    )}
                </div>

                <small className="text-muted">
                    <i className="ri-information-line me-1"></i>
                    Positionnez le code dans le cadre
                </small>
            </div>

            {error && (
                <Alert variant="danger" className="mt-3">
                    <i className="ri-error-warning-line me-2"></i>
                    {error}
                </Alert>
            )}

            <style jsx="true">{`
                .scanner-container {
                    overflow: hidden;
                    border-radius: 8px;
                    background: #000;
                }
                .scan-overlay {
                    pointer-events: none;
                }
                .scan-region {
                    width: 250px;
                    height: 150px;
                }
                .scan-corner {
                    position: absolute;
                    width: 30px;
                    height: 30px;
                    border: 3px solid #00ff00;
                }
                .scan-corner-tl {
                    top: 0;
                    left: 0;
                    border-right: none;
                    border-bottom: none;
                }
                .scan-corner-tr {
                    top: 0;
                    right: 0;
                    border-left: none;
                    border-bottom: none;
                }
                .scan-corner-bl {
                    bottom: 0;
                    left: 0;
                    border-right: none;
                    border-top: none;
                }
                .scan-corner-br {
                    bottom: 0;
                    right: 0;
                    border-left: none;
                    border-top: none;
                }
                .scan-line {
                    position: absolute;
                    width: 100%;
                    height: 2px;
                    background: #00ff00;
                    animation: scan 2s linear infinite;
                    opacity: 0.7;
                }
                @keyframes scan {
                    0% { top: 0; }
                    50% { top: 100%; }
                    100% { top: 0; }
                }
            `}</style>
        </div>
    );
}